import { RelatedVideoLessons } from "@/components/resources/RelatedVideoLessons";
// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-03:
// Independent learning cluster article page: integrity-safe law
// dissertation planning. Fully public. The essay-mill section names the
// trap honestly and never links or names any cheating service. Content
// lives in the data module.

import React from "react";
import Head from "next/head";
import {
  lawDissertationPlanningSections,
  lawDissertationPlanningSources,
} from "@/content/resources/lawDissertationPlanning";
import type { ResourceSection } from "@/content/resources/types";
import {
  ResourceBreadcrumbs,
  ResourceCrossLinks,
  ResourceDisclaimer,
  ResourceHero,
  ResourceSoftCta,
  SectionSourceLinks,
  SourcesSection,
} from "@/components/resources/ResourcePrimitives";

const SITE = "https://www.durmah.ai";

function SectionBlock({ section }: { section: ResourceSection }) {
  return (
    <section id={section.id} className="mb-14 scroll-mt-32">
      <h2 className="mb-4 text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
        {section.heading}
      </h2>
      {section.paragraphs.map((p, i) => (
        <p key={i} className="mb-4 leading-relaxed text-gray-600 dark:text-slate-400">
          {p}
        </p>
      ))}
      {section.bullets && (
        <ul className="mb-4 space-y-3">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 leading-relaxed text-gray-600 dark:text-slate-400">
              <span aria-hidden className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {section.callout && (
        <div className="mt-5 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/70 dark:bg-indigo-500/5 p-6">
          <p className="mb-1.5 text-sm font-black text-indigo-900 dark:text-indigo-300">
            {section.callout.title}
          </p>
          <p className="m-0 text-sm leading-relaxed text-indigo-900/80 dark:text-indigo-200/70">
            {section.callout.body}
          </p>
        </div>
      )}
      <SectionSourceLinks links={section.links} />
    </section>
  );
}

export default function LawDissertationPlanningPage() {
  const canonical = `${SITE}/resources/law-dissertation-planning`;
  const title = "Planning a Law Dissertation: An Integrity-Safe Guide | Durmah";
  const description =
    "How to plan an undergraduate law dissertation: choosing a question rather than a topic, working with your supervisor, research without drowning, structuring early and timelining backwards, using tools with integrity, and why essay mills are a trap, named honestly.";

  const articleSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Planning a Law Dissertation: An Integrity-Safe Guide",
    description,
    url: canonical,
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: "Durmah" },
    publisher: {
      "@type": "Organization",
      name: "Durmah",
      logo: { "@type": "ImageObject", url: `${SITE}/android-chrome-512x512.png` },
    },
    datePublished: "2026-07-10",
    dateModified: "2026-07-10",
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Student Resources", item: `${SITE}/resources` },
      { "@type": "ListItem", position: 2, name: "Law Dissertation Planning", item: canonical },
    ],
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleSchema }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
      </Head>

      <main className="flex-1 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResourceBreadcrumbs current="Law Dissertation Planning" />
          <ResourceHero
            eyebrow="Independent learning"
            title={
              <>
                Law Dissertation{" "}
                <span className="text-indigo-600 dark:text-indigo-400">Planning</span>
              </>
            }
            subtitle="The first piece of legal work where nobody hands you the question. This guide covers choosing a question rather than a topic, getting real value from supervision, researching without drowning, structuring early and timelining backwards, and using modern tools with your integrity intact. Search results for this topic are full of people offering to write it for you; this page is the opposite of that."
          />

          {/* In-page navigation */}
          <nav aria-label="On this page" className="mb-12 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-6">
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-slate-500">
              On this page
            </p>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {lawDissertationPlanningSections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {lawDissertationPlanningSections.map((section) => (
            <SectionBlock key={section.id} section={section} />
          ))}

          <ResourceCrossLinks
            items={[
              {
                title: "Free Legal Research Tools",
                description:
                  "The research stack your dissertation runs on: official free sources and what your library adds.",
                href: "/resources/free-legal-research-tools",
              },
              {
                title: "OSCOLA Referencing Basics",
                description:
                  "Referencing at dissertation length: mechanics, tables, and the workflow that survives deadlines.",
                href: "/resources/oscola-referencing-basics",
              },
              {
                title: "The UK Law Student Guide",
                description:
                  "The stage-by-stage pillar guide, including the final-year context this project sits inside.",
                href: "/resources/uk-law-student-guide",
              },
            ]}
          />

          <RelatedVideoLessons intro="Practise these study methods with short videos, original self-checks and accessible transcripts." slugs={["dissertation-topic-to-question","bibliography-and-research-log","law-dissertation-supervisor-meeting"]} />
          <SourcesSection groups={lawDissertationPlanningSources} />
          <ResourceSoftCta
            title="A research companion that will not write it for you"
            body="Durmah helps you understand the sources you find and pressure-test the argument you are building, grounded in verified authorities and honest about its limits. It will not produce a sentence of your dissertation, because the authorship is the point. This guide stays free either way."
          />
          <ResourceDisclaimer />
        </div>
      </main>
    </div>
  );
}
